export function ocEstadoLabel(estado) {
  if (estado === 1) return 'Por aprobar';
  if (estado === 2) return 'Aprobada';
  if (estado === 3) return 'Rechazada';
  return '';
}

/** Utilidades OC / almacén para inventario de gestión. */

const filtrosInventario = (filtros) => ({
  idProveedor: filtros.idProveedor || '',
  idProducto: filtros.idProducto || '',
  productoIds: filtros.productoIds || [],
  idOrdencompra: filtros.idOrdencompra || '',
});

function escapeRegExp(s) {
  return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function productoNombreSinGrado(producto) {
  let nombre = String(producto?.nombre_producto || '').trim();
  const calidad = String(producto?.calidad || '').trim();
  if (calidad) {
    nombre = nombre.replace(new RegExp(`(^|\\s)${escapeRegExp(calidad)}(?=\\s|$)`, 'i'), ' ');
  }
  return nombre.replace(/\s+/g, ' ').trim();
}

function productoBaseKeyFromCatalog(producto) {
  if (!producto) return '';
  return [
    producto.categoria,
    producto.especie,
    producto.medida,
    producto.espesor,
  ]
    .map((v) => String(v ?? '').trim())
    .join('|');
}

export function productoIdsEquivalentes(productosCatalog, idProducto) {
  if (!idProducto) return [];
  const catalog = productosCatalog || [];
  const ref = catalog.find((p) => Number(p.id_producto) === Number(idProducto));
  const key = productoBaseKeyFromCatalog(ref);
  if (!key) return [Number(idProducto)];
  return catalog
    .filter((p) => productoBaseKeyFromCatalog(p) === key)
    .map((p) => Number(p.id_producto))
    .filter(Number.isFinite);
}

/**
 * OCs que cumplen los filtros activos. `ignorar` excluye un criterio al calcular opciones de ese campo.
 */
export function ordenesCandidatas(ordenes, filtros, ignorar = null) {
  const { idProveedor, idProducto, productoIds, idOrdencompra } = filtrosInventario(filtros);
  const productosSeleccionados = new Set(
    (productoIds?.length ? productoIds : idProducto ? [idProducto] : []).map(Number)
  );
  return (ordenes || []).filter((oc) => {
    if (ignorar !== 'proveedor' && idProveedor && Number(oc.proveedor) !== Number(idProveedor)) {
      return false;
    }
    if (ignorar !== 'producto' && productosSeleccionados.size) {
      const prods = oc.productos || [];
      if (!prods.some((p) => productosSeleccionados.has(Number(p)))) return false;
    }
    if (ignorar !== 'ordencompra' && idOrdencompra && Number(oc.id_ordencompra) !== Number(idOrdencompra)) {
      return false;
    }
    return true;
  });
}

export function proveedoresParaInventario(ordenes, proveedores, filtros) {
  const ids = new Set();
  ordenesCandidatas(ordenes, filtros, 'proveedor').forEach((oc) => {
    if (oc.proveedor != null) ids.add(Number(oc.proveedor));
  });
  return (proveedores || [])
    .filter((p) => ids.has(Number(p.id_proveedor)))
    .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
}

export function productosParaInventario(ordenes, productos, filtros) {
  const ids = new Set();
  ordenesCandidatas(ordenes, filtros, 'producto').forEach((oc) => {
    (oc.productos || []).forEach((p) => ids.add(Number(p)));
  });
  const grupos = new Map();
  (productos || [])
    .filter((p) => ids.has(Number(p.id_producto)))
    .forEach((p) => {
      const nombreBase = productoNombreSinGrado(p);
      const key = nombreBase.toLowerCase();
      if (!grupos.has(key)) {
        grupos.set(key, { ...p, nombre_producto: nombreBase, producto_ids: [] });
      }
      grupos.get(key).producto_ids.push(Number(p.id_producto));
    });
  return [...grupos.values()]
    .sort((a, b) =>
      String(a.nombre_producto || '').localeCompare(String(b.nombre_producto || ''), 'es')
    );
}

export function ordenesCompraParaSeleccion(ordenes, filtros) {
  return ordenesCandidatas(ordenes, filtros, 'ordencompra').sort((a, b) =>
    ocDisplayLabel(a).localeCompare(ocDisplayLabel(b), 'es', { numeric: true })
  );
}

/** @deprecated usar ordenesCompraParaSeleccion */
export function ocsPorProveedorYProducto(ordenes, idProveedor, idProducto) {
  return ordenesCompraParaSeleccion(ordenes, { idProveedor, idProducto, idOrdencompra: '' });
}

/** Etiqueta visible de OC: id_oc → correlativo → fallback id interno. */
export function ocDisplayLabel(oc) {
  if (!oc) return '';
  if (oc.id_oc) return String(oc.id_oc);
  if (oc.correlativo != null && oc.correlativo !== '') return String(oc.correlativo);
  return `OC #${oc.id_ordencompra}`;
}

function fmtDimNum(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return Number.isInteger(n) ? String(n) : String(n);
}

/** Medida faturamento (compra): espesor del producto × ancho/largo del catálogo Medida. */
export function medidaFaturamentoStr(producto, medidas) {
  if (!producto) return '';
  const med = (medidas || []).find((m) => Number(m.id_medida) === Number(producto.medida));
  if (!med || producto.espesor == null || med.ancho == null || med.largo == null) return '';
  return `${fmtDimNum(producto.espesor)} x ${fmtDimNum(med.ancho)} x ${fmtDimNum(med.largo)}`;
}

export function medidasFromOcLine(oc, idProducto, calidades, { productosCatalog = [], medidas = [] } = {}) {
  if (!oc) return { calidades: [], medidasPorGrado: {}, usdPorGrado: {} };
  const calidadesLabels = [];
  const medidasPorGrado = {};
  const usdPorGrado = {};
  const idsEquivalentes = new Set(productoIdsEquivalentes(productosCatalog, idProducto));
  const prods = oc.productos || [];
  const calIds = oc.calidades || [];
  const usdArr = oc.usd_m3 || [];
  const realSize = oc.real_size || [];

  prods.forEach((pid, idx) => {
    if (idsEquivalentes.size && !idsEquivalentes.has(Number(pid))) return;
    if (!idsEquivalentes.size && Number(pid) !== Number(idProducto)) return;
    const calId = calIds[idx];
    const cal = calidades.find((c) => Number(c.id_calidad) === Number(calId));
    const grado = cal?.calidad || '';
    if (grado && !calidadesLabels.includes(grado)) calidadesLabels.push(grado);
    if (grado) {
      const prodCat = (productosCatalog || []).find((p) => Number(p.id_producto) === Number(pid));
      const medStr = medidaFaturamentoStr(prodCat, medidas) || realSize[idx] || '';
      medidasPorGrado[grado.toUpperCase()] = medStr;
      if (usdArr[idx] != null) usdPorGrado[grado.toUpperCase()] = usdArr[idx];
    }
  });

  return { calidades: calidadesLabels, medidasPorGrado, usdPorGrado };
}

export function splitMedida(medidaStr) {
  if (!medidaStr) return { espesor: '', ancho: '', largo: '' };
  const parts = String(medidaStr).replace(/X/g, 'x').split('x').map((p) => p.trim());
  return {
    espesor: parts[0] || '',
    ancho: parts[1] || '',
    largo: parts[2] || '',
  };
}

/** Campos de medida que se propagan a todas las líneas de una NF al editar una fila. */
export const DIM_PROPAGATE_FIELDS = ['espesor', 'ancho', 'largo'];

export function cnpjDigits(v) {
  if (v == null || v === '') return '';
  return String(v).replace(/\D/g, '');
}

/** Resuelve proveedor por id o CNPJ (tax), no por nombre. */
export function proveedorFromContext({ idProveedor, cnpj, proveedores }) {
  const list = proveedores || [];
  if (idProveedor != null && idProveedor !== '') {
    const byId = list.find((p) => Number(p.id_proveedor) === Number(idProveedor));
    if (byId) return byId;
  }
  const digits = cnpjDigits(cnpj);
  if (!digits) return null;
  return list.find((p) => cnpjDigits(p.tax) === digits) || null;
}

/** Indicador del incoterm: 1 = puerto, 2 = almacén (ICD), 3 = door / EXW. */
export function incotermIndicador(incotermId, incoterms) {
  if (incotermId == null || incotermId === '') return null;
  const inc = (incoterms || []).find((i) => Number(i.id_incoterm) === Number(incotermId));
  return inc?.indicador ?? null;
}

const EXW_INCOTERM_ID = 7;

export function opcionesAlmacen({
  incotermId,
  incoterms,
  idProveedor,
  cnpj,
  proveedores,
  almacenes,
  puertos,
}) {
  const indicador = incotermIndicador(incotermId, incoterms);
  const prov = proveedorFromContext({ idProveedor, cnpj, proveedores });

  const puertosBrasil = (puertos || []).filter((p) =>
    String(p.pais || '').toLowerCase().includes('brasil')
  );
  let todosAlmacenes = (almacenes || []).map((a) => a.nombre);
  if (prov?.almacen != null) {
    const def = (almacenes || []).find((a) => Number(a.id_almacen) === Number(prov.almacen));
    if (def?.nombre) {
      todosAlmacenes = [def.nombre, ...todosAlmacenes.filter((n) => n !== def.nombre)];
    }
  }

  if (Number(incotermId) === EXW_INCOTERM_ID) {
    return { tipo: 'exw', opciones: [], editable: true, hint: 'EXW: retiro en fábrica, sin destino.' };
  }

  if (indicador === 1) {
    return {
      tipo: 'puerto',
      opciones: puertosBrasil.map((p) => p.nombre),
      editable: true,
      hint: 'Destino en puerto (Brasil).',
    };
  }

  if (indicador === 2) {
    return {
      tipo: 'almacen',
      opciones: todosAlmacenes,
      editable: true,
      hint: 'Destino en almacén.',
    };
  }

  if (indicador === 3) {
    return {
      tipo: 'libre',
      opciones: [
        ...todosAlmacenes,
        ...(puertos || []).map((p) => `${p.nombre} (${p.pais})`),
      ],
      editable: true,
      hint: '',
    };
  }

  return {
    tipo: 'libre',
    opciones: [
      ...todosAlmacenes,
      ...(puertos || []).map((p) => `${p.nombre} (${p.pais})`),
    ],
    editable: true,
    hint: '',
  };
}

/** Aplica espesor/ancho/largo desde la OC según el grado de la línea (nunca desde XML). */
export function applyDimsFromOcForGrado(linea, ocMedidas, ocUsd, nfBrlPorGrado) {
  if (!linea?.grado) return linea;
  const g = String(linea.grado).toUpperCase();
  const { espesor, ancho, largo } = splitMedida(ocMedidas[g] || '');
  const tieneMapaBrl = nfBrlPorGrado != null;
  const tienePrecioBrl = Object.prototype.hasOwnProperty.call(nfBrlPorGrado || {}, g);
  return {
    ...linea,
    espesor,
    ancho,
    largo,
    precio_usd_unit_m3:
      ocUsd[g] != null && ocUsd[g] !== ''
        ? ocUsd[g]
        : linea.precio_usd_unit_m3,
    precio_brl_unit_m3: tienePrecioBrl
      ? nfBrlPorGrado[g]
      : tieneMapaBrl
        ? ''
        : linea.precio_brl_unit_m3,
    total_brl_m3: tieneMapaBrl && !tienePrecioBrl ? null : linea.total_brl_m3,
  };
}

/** Conserva el precio R$/M³ original de cada grado para poder invertir selecciones. */
export function preciosBrlPorGrado(lineas, existentes = {}) {
  const precios = { ...(existentes || {}) };
  for (const linea of lineas || []) {
    const grado = String(linea?.grado || '').trim().toUpperCase();
    const precio = linea?.precio_brl_unit_m3;
    if (!grado || precio == null || precio === '') continue;
    if (!Object.prototype.hasOwnProperty.call(precios, grado)) precios[grado] = precio;
  }
  return precios;
}

/** Rellena medidas vacías desde OC por grado y, si aún faltan, desde la primera línea con datos. */
export function unifyLineaDims(lineas, medidasPorGrado, usdPorGrado) {
  const ref = (lineas || []).find((ln) => ln.espesor && ln.ancho && ln.largo);
  return (lineas || []).map((ln) => {
    let updated = { ...ln };
    if (!updated.espesor || !updated.ancho || !updated.largo) {
      updated = fillDimFromGradoIfEmpty(updated, medidasPorGrado, usdPorGrado);
    }
    if (!ref) return updated;
    return {
      ...updated,
      espesor: updated.espesor || ref.espesor,
      ancho: updated.ancho || ref.ancho,
      largo: updated.largo || ref.largo,
    };
  });
}

export function dimensionesOpcionesPorOc(oc, idProducto, calidades, ocCatalogos = {}) {
  const { medidasPorGrado, calidades: cals } = medidasFromOcLine(
    oc,
    idProducto,
    calidades,
    ocCatalogos
  );
  const espesores = new Set();
  const anchos = new Set();
  const largos = new Set();
  Object.values(medidasPorGrado).forEach((m) => {
    const d = splitMedida(m);
    if (d.espesor) espesores.add(d.espesor);
    if (d.ancho) anchos.add(d.ancho);
    if (d.largo) largos.add(d.largo);
  });
  return {
    calidades: cals,
    medidasPorGrado,
    espesores: [...espesores],
    anchos: [...anchos],
    largos: [...largos],
  };
}

export function ocMedidasForNota(nota, ordenes, calidades, ocCatalogos = {}) {
  const oc = (ordenes || []).find((o) => Number(o.id_ordencompra) === Number(nota?.id_ordencompra));
  return dimensionesOpcionesPorOc(oc, nota?.id_producto, calidades, ocCatalogos);
}

export function enrichNotaConOc(nota, ordenes, calidades, ocCatalogos = {}) {
  if (!nota) return nota;
  const oc = (ordenes || []).find((o) => Number(o.id_ordencompra) === Number(nota.id_ordencompra));
  const { medidasPorGrado, usdPorGrado, calidades: cals } = medidasFromOcLine(
    oc,
    nota?.id_producto,
    calidades,
    ocCatalogos
  );
  const dims = dimensionesOpcionesPorOc(oc, nota?.id_producto, calidades, ocCatalogos);
  const brlPorGrado = preciosBrlPorGrado(nota.lineas, nota._brlPorGrado);
  const lineas = (nota.lineas || []).map((ln) =>
    fillDimFromGradoIfEmpty(ln, medidasPorGrado, usdPorGrado)
  );
  return {
    ...nota,
    oc_label: oc ? ocDisplayLabel(oc) : nota.oc_label ?? '',
    calidades_oc: nota.calidades_oc?.length ? nota.calidades_oc : cals.length ? cals : dims.calidades,
    castigo_rw_default: nota.castigo_rw_default ?? (oc?.allowance != null ? oc.allowance : ''),
    _dimEsp: dims.espesores,
    _dimAnc: dims.anchos,
    _dimLar: dims.largos,
    _medidasPorGrado: medidasPorGrado,
    _usdPorGrado: usdPorGrado,
    _brlPorGrado: brlPorGrado,
    lineas,
  };
}

/** Rellena espesor/ancho/largo desde la OC solo si la línea aún no tiene valor. */
export function fillDimFromGradoIfEmpty(linea, ocMedidas, ocUsd) {
  if (!linea?.grado) return linea;
  const g = String(linea.grado).toUpperCase();
  const { espesor, ancho, largo } = splitMedida(ocMedidas[g] || '');
  return {
    ...linea,
    espesor: linea.espesor || espesor,
    ancho: linea.ancho || ancho,
    largo: linea.largo || largo,
    precio_usd_unit_m3:
      linea.precio_usd_unit_m3 !== '' && linea.precio_usd_unit_m3 != null
        ? linea.precio_usd_unit_m3
        : ocUsd[g] ?? linea.precio_usd_unit_m3,
  };
}

export function applyGradoToLinea(linea, grado, ocMedidas, ocUsd, nfBrlPorGrado) {
  return applyDimsFromOcForGrado(
    { ...linea, grado },
    ocMedidas,
    ocUsd,
    nfBrlPorGrado
  );
}
